import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQuery } from 'shared/lib/redux';
import { loggerService } from 'shared/lib/logger';

import {
  AssignmentsQueryTagTypes,
  getAssignmentServiceUrl,
  SSC_QUERY_PARAM,
} from './constants';
import { AssignmentWithUnknownDomain, normalizeAssignments } from './lib';
import { AssignmentsPayload, EditAssignmentPayload } from './types';

export const assignmentServiceApi = createApi({
  reducerPath: 'assignment-service',
  baseQuery: baseQuery(getAssignmentServiceUrl),
  refetchOnMountOrArgChange: true,
  tagTypes: [AssignmentsQueryTagTypes.ASSIGNMENT],
  endpoints: (builder) => ({
    getClassAssignments: builder.query<
      AssignmentWithUnknownDomain[] | null,
      {
        classesBks: string[];
        userBk?: string;
        offset?: number;
        limit?: number;
        showPending?: boolean;
        pendingOnly?: boolean;
        includeSingleSessions?: boolean;
      }
    >({
      query: ({
        classesBks,
        userBk,
        offset,
        limit,
        showPending = false,
        pendingOnly = false,
        includeSingleSessions = false,
      }) => {
        // return if there are no selected classes
        if (!classesBks.length) return null;

        const showPendingQueryParam = `showPending=${showPending}`;
        const offsetQueryParam = offset ? `&offset=${offset}` : '';
        const limitQueryParam = limit ? `&limit=${limit}` : '';
        const pendingOnlyQueryParam = `&pendingOnly=${pendingOnly}`;
        const sscAssignmentsParam =
          includeSingleSessions && userBk
            ? `&${SSC_QUERY_PARAM}=${userBk}`
            : '';

        const queryParams = [
          showPendingQueryParam,
          offsetQueryParam,
          limitQueryParam,
          pendingOnlyQueryParam,
          sscAssignmentsParam,
        ];

        return {
          url: `class/batch-get?${queryParams.join('')}`,
          method: 'POST',
          body: classesBks,
        };
      },
      transformResponse: normalizeAssignments,
      providesTags: () => [
        {
          type: AssignmentsQueryTagTypes.ASSIGNMENT,
        },
      ],
    }),
    editAssignment: builder.mutation<AssignmentsPayload, EditAssignmentPayload>(
      {
        query: (editedAssignmentObj) => ({
          url: '/',
          method: 'PUT',
          body: editedAssignmentObj,
        }),
        async onQueryStarted(editedAssignmentObj, { queryFulfilled }) {
          try {
            await queryFulfilled;
          } catch (e) {
            loggerService.logError(
              `Error editing the assignment ${editedAssignmentObj.businessKey}`,
              e,
            );
          }
        },
      },
    ),
    createAssignment: builder.mutation<
      AssignmentsPayload,
      EditAssignmentPayload
    >({
      query: (editedAssignmentObj) => ({
        url: '/',
        method: 'POST',
        body: editedAssignmentObj,
      }),
      async onQueryStarted(editedAssignmentObj, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (e) {
          loggerService.logError(
            `Error during creating the assignment ${editedAssignmentObj.businessKey}`,
            e,
          );
        }
      },
    }),
    deleteAssignment: builder.mutation<null, string>({
      query: (assignmentKey) => ({
        url: `${assignmentKey}`,
        method: 'DELETE',
      }),
      async onQueryStarted(assignmentKey, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (e) {
          loggerService.logError(
            `Error during deleting the assignment ${assignmentKey}`,
            e,
          );
        }
      },
    }),
  }),
});

export const {
  useGetClassAssignmentsQuery,
  useEditAssignmentMutation,
  useCreateAssignmentMutation,
  useDeleteAssignmentMutation,
} = assignmentServiceApi;
